<template>
  <div class="content" ref="root">
    <img :src="resolvePath(require('@/assets/img/poster/alvida.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/poster-09.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/don-krieg.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/poster-03.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/poster-01.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/alvida.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/poster-09.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/don-krieg.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/poster-03.webp'))" alt="" />
    <img :src="resolvePath(require('@/assets/img/poster/poster-01.webp'))" alt="" />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { gsap } from 'gsap';
import { resolvePath } from '@/utils/Path';

const root = ref(null);

const MathUtils = {
  lerp: (a, b, n) => (1 - n) * a + n * b,
  distance: (x1, y1, x2, y2) => Math.hypot(x2 - x1, y2 - y1),
};

let mousePos = { x: 0, y: 0 };
let lastMousePos = { x: 0, y: 0 };
let cacheMousePos = { x: 0, y: 0 };

const getMousePos = (ev) => {
  let posx = 0;
  let posy = 0;
  if (!ev) ev = window.event;
  if (ev.pageX || ev.pageY) {
    posx = ev.pageX;
    posy = ev.pageY;
  } else if (ev.clientX || ev.clientY) {
    posx = ev.clientX + document.body.scrollLeft + document.documentElement.scrollLeft;
    posy = ev.clientY + document.body.scrollTop + document.documentElement.scrollTop;
  }
  return { x: posx, y: posy };
};

const getMouseDistance = () =>
  MathUtils.distance(mousePos.x, mousePos.y, lastMousePos.x, lastMousePos.y);

class Image {
  constructor(el) {
    this.el = el;
    this.defaultStyle = {
      scale: 1,
      x: 0,
      y: 0,
      opacity: 0,
    };
    this.getRect();
    this.initEvents();
  }

  initEvents() {
    window.addEventListener('resize', this.resize);
  }

  destroyEvents() {
    window.removeEventListener('resize', this.resize);
  }

  resize() {
    gsap.set(this.el, this.defaultStyle);
    this.getRect();
  }

  getRect() {
    this.rect = this.el.getBoundingClientRect();
  }

  isActive() {
    return gsap.isTweening(this.el) || this.el.style.opacity != 0;
  }
}

const imageTrail = ref(null);

onMounted(async() => {
  await nextTick();
  imageTrail.value = new ImageTrail();
  imageTrail.value.init();

  // Update mouse position
  document.addEventListener('mousemove', (ev) => mousePos = getMousePos(ev));
})

onBeforeUnmount(() => {
  imageTrail.value?.destroy();
  document.removeEventListener('mousemove', (ev) => mousePos = getMousePos(ev));
})

class ImageTrail {
  constructor() {
    this.content = ref(null);
    this.images = [];
    this.imagesTotal = 0;
    this.imgPosition = 0;
    this.zIndexVal = 1;
    this.threshold = 100;
    this.init();
  }

  init() {
    this.content.value = root.value;
    const imageElements = this.content.value.querySelectorAll('img');
    this.imagesTotal = imageElements.length;
    imageElements.forEach((img) => this.images.push(new Image(img)));

    this.render();
  }

  destroy() {
    this.images.forEach((img) => img.destroyEvents());
  }

  render = () => {
    let distance = getMouseDistance();
    cacheMousePos.x = MathUtils.lerp(cacheMousePos.x || mousePos.x, mousePos.x, 0.1);
    cacheMousePos.y = MathUtils.lerp(cacheMousePos.y || mousePos.y, mousePos.y, 0.1);

    if (distance > this.threshold) {
      this.showNextImage();

      ++this.zIndexVal;
      this.imgPosition = this.imgPosition < this.imagesTotal - 1 ? this.imgPosition + 1 : 0;

      lastMousePos = mousePos;
    }

    let isIdle = true;
    for (let img of this.images) {
      if (img.isActive()) {
        isIdle = false;
        break;
      }
    }

    if (isIdle && this.zIndexVal !== 1) {
      this.zIndexVal = 1;
    }

    requestAnimationFrame(this.render);
  }

  showNextImage() {
    const img = this.images[this.imgPosition];
    gsap.killTweensOf(img.el);

    const tl = gsap.timeline()
    tl
      .set(img.el, {
        startAt: { opacity: 0, scale: 1 },
        opacity: 1,
        scale: 0,
        zIndex: this.zIndexVal,
        x: cacheMousePos.x - img.rect.width / 2,
        y: cacheMousePos.y - img.rect.height / 2,
      }, 0)
      .to(img.el, {
        x: mousePos.x - img.rect.width / 2,
        y: mousePos.y - img.rect.height / 2,
        scale: 1,
        duration: 1,
        ease: 'expo.out',
      }, 0)
      .to(img.el, {
        opacity: 0,
        duration: 1,
        ease: 'quart.out',
      }, 0.75)
      .to(img.el, {
        scale: 0.2,
        duration: 1,
        ease: 'quart.out',
      }, 0.75);
  }
}
</script>

<style lang="stylus" scoped>
img
  max-width: rem(300);
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  will-change: transform;
</style>